import type {DeadStockSnapshotResponse} from '@prompt-bridge/shared/validations';

/**
 * Folds a freshly fetched page into the cached response.
 *
 * `offset` is deliberately absent from the cache key so that pages of one filter set
 * accumulate into a single entry. That makes the first page a special case: it can arrive
 * against a *warm* entry, because RTK refetches whenever the offset differs from the
 * previous one — and it differs on the way back down to 0, not only on the way up.
 * Appending it then duplicated every row already held, and since the next offset is derived
 * from the accumulated length, the list went on to skip that many server rows.
 *
 * Mutates `cache` for a later page and returns a replacement for the first one, which is the
 * contract RTK Query's `merge` accepts.
 */
export function mergeDeadStockSnapshotPage(
  cache: DeadStockSnapshotResponse,
  incoming: DeadStockSnapshotResponse,
  offset: number | undefined
) {
  if ((offset ?? 0) === 0) {
    return incoming;
  }

  cache.cards.push(...incoming.cards);
  // The scalars describe the whole population, so the newest read wins.
  cache.buckets = incoming.buckets;
  cache.totalCardCount = incoming.totalCardCount;
  cache.invalidBalanceCount = incoming.invalidBalanceCount;

  return undefined;
}
